<script lang="ts" setup>
import { InputMask, NullSetter, ValidatorMessage } from '@/components';
import { useHelper, useValidator } from '@/composeables';
import InputMaskData from '@/objects/InputMaskData';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import type { IEditorComponent, ValidatorDef } from '@/objects/gui';
import { parseTimespanIntoStringFromFormat, type TimeType } from '@/utilities/dateutils';
import { computed, ref } from 'vue';

const props = withDefaults(
    defineProps<{
        modelValue: ITimeSpan | null;

        /**
         * The format to use.
         */
        format?: TimeType;

        /**
         * The validator for the input
         * @default null
         */
        validator?: ValidatorDef | null;

        /**
         * If the nullsetter should be shown
         * @default false
         * */
        showNullSetter?: boolean;

        /**
         * The input mode. If we only allow positive or negative values or both
         * @default both
         * TODO: This needs to be implemented when we need it
         */
        mode?: 'positive' | 'negative' | 'both';

        /**
         * If the input is readonly
         * @default false
         */
        readonly?: boolean;
    }>(),
    {
        format: 'TIMEMS3',
        validator: null,
        showNullSetter: false,
        mode: 'both',
        readonly: false,
    },
);

const emits = defineEmits<{
    (e: 'update:modelValue', val: ITimeSpan | null): void;
    (e: 'change', newval: ITimeSpan, oldval: ITimeSpan | null): void;
}>();

const inputMaskInstance = ref<InstanceType<typeof InputMask> | null>(null);

// The value that should be shown in the mask input
const internalMaskValue = ref<string>('');

const internalTimespan = ref<ITimeSpan | null>(props.modelValue);

// Computed to sync the inputMask and the datePicker
const inputMaskValue = computed({
    get() {
        return (props.modelValue?.IsNegative ? '' : '+') + parseTimespanIntoStringFromFormat(props.modelValue, props.format);
    },
    set(value) {
        internalMaskValue.value = value ?? '';
        internalTimespan.value = value === null ? null : TimeSpan.FromTimeString(value);
        emits('change', internalTimespan.value!, props.modelValue);
    },
});

// Create an inputMask for the current date format
let imd = InputMaskData.CreateFromTimeSpan(props.format);

// Do we need any validator??
const { Validate, ValidateMessage } = useValidator(props.validator, inputMaskValue.value)!;

/**
 * We emit the new modelvalue and it is either null or a timespan
 * @param val
 */
const emitStringAsTimespan = (val: string | null) => {
    if (val === null) {
        emits('update:modelValue', null);
        return;
    }

    emits('update:modelValue', TimeSpan.FromTimeString(val));
};

// We watch the modelvalue and update the internal value
const maskValueChanged = (newVal: string) => {
    if (!imd.isMaskFilled()) {
        emitStringAsTimespan(null);
    }

    inputMaskValue.value = newVal;
};

/**
 * We handle the keydown event to allow for stepping the value
 * @param event
 */
const onKeyDown = (event: KeyboardEvent) => {
    // We check if the key is either arrowdown or arrowup
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        // Arrow up and down skip one second, hold shift and skip 5 seconds and CTRL and skip 30 seconds
        let step = event.ctrlKey ? 30000 : event.shiftKey ? 5000 : 1000;

        internalTimespan.value = TimeSpan.FromMilliseconds(
            internalTimespan.value!.TotalMilliseconds + (event.key === 'ArrowUp' ? step : -step),
        );

        emits('update:modelValue', internalTimespan.value);

        return useHelper().stopPropagation(event);
    }
};

/**
 * The exposed component interface
 */
defineExpose<IEditorComponent>({
    focus: () => inputMaskInstance.value?.focus(),
    validateMessage: computed(() => null),
    KeyboardEvent: (ev: KeyboardEvent) => {
        if (inputMaskInstance.value && typeof inputMaskInstance.value.KeyboardEvent === 'function') {
            // We do it twice because the first time the prefix +/- is handled and the second time the actual number
            inputMaskInstance.value?.KeyboardEvent(ev);
            inputMaskInstance.value?.KeyboardEvent(ev);
        }
    },
});
</script>

<template>
    <ValidatorMessage
        :message="ValidateMessage"
        :fill="false"
    >
        <div class="form-control-calendar flex-nowrap">
            <div
                class="btn-group"
                role="group"
            >
                <NullSetter
                    :nullable="showNullSetter"
                    v-model="inputMaskValue"
                >
                    <InputMask
                        v-bind:model-value="inputMaskValue"
                        class="text-end font-monospace text-lowercase"
                        :class="$attrs.class"
                        ref="inputMaskInstance"
                        :readonly="readonly"
                        @update:model-value="emitStringAsTimespan"
                        @mask-value-changed="maskValueChanged"
                        @keydown="onKeyDown"
                        :mask-data="imd"
                    />
                </NullSetter>
            </div>
        </div>
    </ValidatorMessage>
</template>
